// Shared constants for ResumeTab components

// Skills data - common programming skills and technologies
export const SKILLS_OPTIONS = [
    'JavaScript', 'TypeScript', 'Python', 'Java', 'C++', 'C', 'C#', 'Go', 'Rust',
    'Ruby', 'PHP', 'Swift', 'Kotlin', 'Scala', 'R', 'MATLAB', 'SQL', 'HTML5', 'CSS3',
    'React', 'Vue.js', 'Angular', 'Next.js', 'Node.js', 'Express.js', 'Django', 'Flask',
    'Spring Boot', 'FastAPI', 'GraphQL', 'REST', 'PostgreSQL', 'MySQL', 'MongoDB',
    'Redis', 'Elasticsearch', 'Docker', 'Kubernetes', 'AWS', 'GCP', 'Azure',
    'Git', 'GitHub Actions', 'Jenkins', 'Terraform', 'Ansible', 'Machine Learning',
    'Deep Learning', 'TensorFlow', 'PyTorch', 'NLP', 'Computer Vision', 'Data Science',
    'Pandas', 'NumPy', 'Scikit-learn', 'Apache Spark', 'Hadoop', 'Kafka',
    'Microservices', 'System Design', 'Agile', 'Scrum', 'Linux', 'Bash',
];

// Coding profile platforms - API uses uppercase enum values
export const PROFILE_PLATFORMS = [
    { id: 'LEETCODE', name: 'LeetCode', icon: '🔗', placeholder: 'leetcode-username', urlBase: 'https://leetcode.com/' },
    { id: 'GITHUB', name: 'GitHub', icon: '🐙', placeholder: 'github-username', urlBase: 'https://github.com/' },
    { id: 'CODECHEF', name: 'CodeChef', icon: '👨‍🍳', placeholder: 'codechef-username', urlBase: 'https://www.codechef.com/users/' },
    { id: 'CODEFORCES', name: 'Codeforces', icon: '📊', placeholder: 'codeforces-username', urlBase: 'https://codeforces.com/profile/' },
    { id: 'HACKERRANK', name: 'HackerRank', icon: '💻', placeholder: 'hackerrank-username', urlBase: 'https://www.hackerrank.com/profile/' },
    { id: 'GITLAB', name: 'GitLab', icon: '🦊', placeholder: 'gitlab-username', urlBase: 'https://gitlab.com/' },
    { id: 'BITBUCKET', name: 'Bitbucket', icon: '🪣', placeholder: 'bitbucket-username', urlBase: 'https://bitbucket.org/' },
];

// Link types for the links API
export const LINK_TYPES = [
    { id: 'PORTFOLIO', label: 'Portfolio', placeholder: 'https://yourportfolio.com' },
    { id: 'PERSONAL', label: 'Personal Website', placeholder: 'https://yoursite.com' },
    { id: 'BLOG', label: 'Blog', placeholder: 'https://yourblog.com' },
    { id: 'SOCIAL', label: 'Social Media', placeholder: 'https://twitter.com/username' },
    { id: 'OTHER', label: 'Other', placeholder: 'https://example.com' },
];

// Month names
export const MONTHS = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
];

// Generate year options dynamically (current year - 70 to current year + 10)
const generateYearRange = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear + 10; year >= currentYear - 70; year--) {
        years.push({ value: String(year), label: String(year) });
    }
    return years;
};

export const YEAR_RANGE = generateYearRange();

// Generate day options (1-31)
export const DAYS = Array.from({ length: 31 }, (_, i) => ({
    value: String(i + 1).padStart(2, '0'),
    label: String(i + 1),
}));

// Year options for dropdowns (Month Year format for start/end dates)
const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                   'July', 'August', 'September', 'October', 'November', 'December'];
    
    for (let year = currentYear + 10; year >= currentYear - 70; year--) {
        for (let month = 11; month >= 0; month--) {
            years.push({ label: `${months[month]} ${year}`, value: `${year}-${String(month + 1).padStart(2, '0')}` });
        }
    }
    return years;
};

export const YEAR_OPTIONS = generateYearOptions();

// Helper function to parse YYYY-MM-DD date string into components
export const parseDateString = (dateStr) => {
    if (!dateStr) return { day: '', month: '', year: '' };
    
    // Handle YYYY-MM-DD format
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
        return { year: match[1], month: match[2], day: match[3] };
    }
    
    // Handle YYYY-MM format (for start/end dates)
    const monthYearMatch = dateStr.match(/^(\d{4})-(\d{2})$/);
    if (monthYearMatch) {
        return { year: monthYearMatch[1], month: monthYearMatch[2], day: '01' };
    }
    
    return { day: '', month: '', year: '' };
};

// Helper function to format date components to YYYY-MM-DD
export const formatDateComponents = (day, month, year) => {
    if (!year || !month) return '';
    const d = day || '01';
    return `${year}-${month.padStart(2, '0')}-${d.padStart(2, '0')}`;
};

// Helper function to format date components to YYYY-MM (for start/end dates)
export const formatMonthYear = (month, year) => {
    if (!year || !month) return '';
    return `${year}-${month.padStart(2, '0')}`;
};

// Helper function to display date as "Month Year" or "Day Month Year"
export const formatDateDisplay = (dateStr, includeDay = false) => {
    if (!dateStr) return '';
    const { day, month, year } = parseDateString(dateStr);
    if (!year || !month) return '';
    
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const monthName = monthNames[parseInt(month, 10) - 1];
    
    if (includeDay && day && day !== '01') {
        return `${parseInt(day, 10)} ${monthName} ${year}`;
    }
    return `${monthName} ${year}`;
};
